import _ from "lodash";
import {
  getTransactionType,
  getPortfolioComponentsWithSubDepots,
  instrCustodianData,
  setEarliestPriceDates,
  setInstrumentBankData,
  setMPInstrumentBankData,
  confirmPassiveDepotTrading,
} from "../../../utils";
import {TRANSACTION_INSTR, TRANSACTION_TYPE_VALUES} from "../../../constants";
import {
  EinzeltitelTradingInstrumentsHandler,
  EinzeltitelTradingSwitchInInstrumentsHandler,
  getRiskProfilingAnswers,
  TradingSellInstrumentsHandler,
  TradingSwitchOutInstrumentsHandler,
} from '../../../../RiskProfiling/utils';
import {TRADING_ACTIONS} from '../../../../../components/Charts/InstrumentsAllocationTable/constants';
import {getAssetInternalId} from "../../../../CustomerDashboard/utils";
import {prepareSwitches} from "../../TradeStep/components/PortfolioTrade/utils";
import {fetchAssetPrice} from "./PortfolioTradeV2/utils";


/**
 * Creates instrument handlers for a portfolio based on its configuration.
 * Pure factory — no hooks, no state.
 */
export const createInstrumentHandlers = (portfolio, dataService, isVirtual) => {
  let addInstrumentHandler, sellInstrumentHandler, switchOutInstrumentHandler;

  if (!isVirtual) {
    let disableFunds = undefined;
    if (portfolio.depotType) {
      portfolio.available_asset_classes = portfolio.depotType.configuration.hasOwnProperty('available_asset_classes')
        ? portfolio.depotType.configuration.available_asset_classes
        : portfolio.depot_available_asset_classes;
      disableFunds = portfolio.depotType.configuration.trading_funds_disabled;
    }

    const onlyFunds = _.isEmpty(portfolio.available_asset_classes);

    addInstrumentHandler = new EinzeltitelTradingInstrumentsHandler(
      portfolio.companyId,
      dataService._srri,
      dataService.targetMarket,
      onlyFunds, undefined, portfolio,
      dataService.user && dataService.user.bca_level_id,
      dataService.serviceConcept,
      getRiskProfilingAnswers(dataService, portfolio.available_asset_classes),
      disableFunds);

    sellInstrumentHandler = new TradingSellInstrumentsHandler(
      portfolio.companyId, undefined, dataService.targetMarket,
      onlyFunds, undefined, undefined, disableFunds, portfolio,
      dataService.user && dataService.user.bca_level_id);

    switchOutInstrumentHandler = new TradingSwitchOutInstrumentsHandler(
      portfolio.companyId, undefined, dataService.targetMarket,
      undefined, undefined, undefined, disableFunds);
  }

  return {
    [TRANSACTION_TYPE_VALUES.BUY]: addInstrumentHandler,
    [TRANSACTION_TYPE_VALUES.SELL]: sellInstrumentHandler,
    [TRANSACTION_TYPE_VALUES.SWITCH]: switchOutInstrumentHandler
  };
};

/**
 * Creates a switch-in instrument handler.
 */
export const createSwitchInHandler = (portfolio, dataService, isVirtual) => {
  if (isVirtual) return undefined;
  return new EinzeltitelTradingSwitchInInstrumentsHandler(
    portfolio.companyId, portfolio.available_asset_classes, undefined, dataService.targetMarket
  );
};

/**
 * Gets available portfolio instruments for a given transaction type.
 */
export const getPortfolioInstruments = (portfolio, transactions, isVirtual, key) => {
  const isAssetsTrading = ![TRANSACTION_TYPE_VALUES.REBALANCING, TRANSACTION_TYPE_VALUES.PORTFOLIO_BUILDER].includes(key);

  if (!isAssetsTrading) {
    return portfolio.data.components || [];
  }

  if (portfolio.isModelportfolio) {
    // Model portfolios allow only one transaction type at a time (buy OR sell, not both)
    const alreadyTraded = _.flatten(
      [TRANSACTION_TYPE_VALUES.BUY, TRANSACTION_TYPE_VALUES.SELL, TRANSACTION_TYPE_VALUES.SWITCH]
        .map(t => _.get(transactions, t) || [])
    );

    return _.isEmpty(alreadyTraded) ? [{...portfolio.data}] : [];
  }

  const transactionToCheck = [TRANSACTION_TYPE_VALUES.BUY];
  if ([TRANSACTION_TYPE_VALUES.SELL, TRANSACTION_TYPE_VALUES.SWITCH].includes(key)) {
    transactionToCheck.push(key);
  }
  const alreadyTraded = _.flatten(transactionToCheck.map(t => _.get(transactions, t) || []));

  let components = _.get(portfolio, 'data.components', []);
  if (key === TRANSACTION_TYPE_VALUES.BUY) {
    components = _.concat(components, _.get(portfolio, 'data.soldAssets') || []);
  }
  let instruments = getPortfolioComponentsWithSubDepots(components)
    .filter(i => !!getAssetInternalId(i) && (isVirtual || !alreadyTraded.map(i => getAssetInternalId(i.data)).includes(getAssetInternalId(i))));
  return [TRANSACTION_TYPE_VALUES.SELL, TRANSACTION_TYPE_VALUES.SWITCH].includes(key)
    ? instruments.filter(i => i.quantity > 0)
    : instruments;
};

/**
 * Adds instruments to portfolio transactions.
 * Returns the curried function matching the original handleAddItems signature.
 */
export const createHandleAddItems = ({
  portfolio,
  transactions,
  isVirtual,
  isPassiveDepot,
  handleTransactionValueChange,
  handleSwitchAmountEuroChanged,
  handleNewVirtualTransactionWeightTypeChanged,
  handleChange,
  handlePortfolioChange,
  riskCheck,
  customerId,
  dispatch,
  resource,
  modalContext,
  onModalClose,
}) => {
  const findAssetIndex = (assets, asset) => _.findIndex(assets, a => getAssetInternalId(a.data) == getAssetInternalId(asset));

  return (key, switchItem) => async (selectedAssets, triggerChange = true, skipRiskCheck = false) => {
    if (isPassiveDepot && !await confirmPassiveDepotTrading(modalContext)) {
      return false;
    }

    const isSwitch = !!switchItem;

    if (isSwitch) {
      key = TRANSACTION_TYPE_VALUES.BUY;
      _.unset(switchItem, 'errors.buy');
    }

    if (!skipRiskCheck && key === TRANSACTION_TYPE_VALUES.BUY && riskCheck) {
      if (!await riskCheck(selectedAssets)) {
        return false;
      }
    }

    const parentObj = isSwitch ? switchItem : transactions;
    const tradingAction = TRADING_ACTIONS.find(o => o.code === key).value;
    const newInstruments = [];

    selectedAssets.map(item => {
      if (findAssetIndex(parentObj[key], item) === -1) {
        const instrument = {
          data: item,
          transaction_type: ((key == TRANSACTION_TYPE_VALUES.SWITCH || isSwitch) && TRANSACTION_INSTR.qty) || getTransactionType(portfolio, key),
          tradingType: tradingAction,
          transaction_value: null
        };

        if (item.hasOwnProperty('newMarketValue')) {
          if (isSwitch) {
            handleSwitchAmountEuroChanged(instrument, item.newMarketValue, switchItem);
          } else {
            if (item.hasOwnProperty('newTransactionType')) {
              instrument.transaction_type = item.newTransactionType;
            }
            handleTransactionValueChange(instrument, item.newMarketValue, undefined, true, false);
          }
        }

        if (key === TRANSACTION_TYPE_VALUES.SWITCH && !switchItem) instrument.buy = [];
        setInstrumentBankData(instrument, instrCustodianData(item, portfolio.companyId), true);
        // FIXME: we should pass changes, not mutate
        parentObj[key] = [...parentObj[key], instrument];
        newInstruments.push(instrument);

        if (portfolio.isModelportfolio) {
          setMPInstrumentBankData(instrument, portfolio);
          portfolio.tradingType = tradingAction;
        }

        if (isVirtual && isSwitch) {
          fetchAssetPrice(customerId, instrument, switchItem.deposit_from_date, dispatch, handleChange, false, resource);
        }
      }
    });

    if (newInstruments.length && isVirtual) {
      await setEarliestPriceDates(newInstruments, dispatch);
    }

    handleNewVirtualTransactionWeightTypeChanged && handleNewVirtualTransactionWeightTypeChanged(portfolio.transactionsWeightType, false);
    onModalClose && onModalClose(isSwitch);

    if (triggerChange && handlePortfolioChange) {
      const changes = {...portfolio.transactions};
      if(isSwitch){
        changes[TRANSACTION_TYPE_VALUES.SWITCH] = [...changes[TRANSACTION_TYPE_VALUES.SWITCH]];
      }
      handlePortfolioChange(portfolio, {transactions: changes});
    }
  };
};

/**
 * Prepares buy/sell recommendations by matching into switches where possible.
 */
export const prepareRecommendationsForTransactions = async ({
  buy, sell, portfolio, allowedTradeOptions, switchOutHandler, switchInHandler
}) => {
  let switchOutCandidate = [], switchOutExcluded;
  let switchInCandidate = [], switchInExcluded;
  let switches = [];

  if (allowedTradeOptions.includes(TRANSACTION_TYPE_VALUES.SWITCH) && switchOutHandler && switchInHandler) {
    [switchOutCandidate, switchOutExcluded] = await switchOutHandler.getCustodianAndFilterInstruments(sell, true);
    [switchInCandidate, switchInExcluded] = await switchInHandler.getCustodianAndFilterInstruments(buy, true);

    switchOutExcluded.concat(switchInExcluded).forEach((a) => {
      delete a.disabledByFilter;
      delete a.disabledByAcceptanceCriterias;
    });

    if (!_.isEmpty(switchOutCandidate) && !_.isEmpty(switchInCandidate)) {
      prepareSwitches(portfolio, switches, switchOutCandidate, switchOutExcluded, switchInCandidate);
    } else {
      switchOutExcluded = switchOutExcluded.concat(switchOutCandidate);
    }

    sell = switchOutExcluded;
    buy = switchInExcluded.concat(switchInCandidate.filter((b) => !!b.newMarketValue && !b.newMarketValueUsed));
  }

  return [buy, sell, switches];
};

/**
 * Adds prepared recommendations (buy, sell, switches) to transactions.
 */
export const addRecommendationsToTransactions = async ({buy, sell, switches, handleAddItems, transactions, riskCheck}) => {
  let switchInsAndBuys = [...buy];
  let switchOuts = [];
  switches.forEach((switchData) => {
    switchInsAndBuys = [...switchInsAndBuys, ...switchData.switchIns];
    switchOuts.push(switchData.switchOutItem);
  });

  if (riskCheck && !await riskCheck(switchInsAndBuys)) {
    return;
  }

  if (!_.isEmpty(switchOuts)) {
    await handleAddItems(TRANSACTION_TYPE_VALUES.SWITCH)(switchOuts, false);
  }

  for (const switchData of switches) {
    const switchOutItem = _.find(transactions[TRANSACTION_TYPE_VALUES.SWITCH], t => t.data.isin === switchData.switchOutItem.isin);
    await handleAddItems(TRANSACTION_TYPE_VALUES.SWITCH, switchOutItem)(switchData.switchIns, false, true);
  }

  await handleAddItems(TRANSACTION_TYPE_VALUES.BUY)(buy, false, true);
  await handleAddItems(TRANSACTION_TYPE_VALUES.SELL)(sell, false);
};
